import { useState, useEffect, useMemo, useCallback, ReactNode } from 'react'
import { isPlainObject } from 'lodash-es'
import { useQuery, type Context } from '@apollo/client'
import { sortBy, uniqBy, castArray, some, isString, isArray } from 'lodash-es'
import { type WatchQueryFetchPolicy } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { buildSearch } from '@/helpers/build-query-variables'
import makeLoadMore from '@/helpers/makeLoadMore'
import { Form, FilterDropdown } from '@/ui'
import ScrollDetector from '@/components/ScrollDetector'
import ErrorLabel from './ErrorLabel'
import { batchClient } from '@/components/App/client'

export type FieldQueryType = {
  searchFields?: string[]
  idField?: string
  query: any
  searchQuery?: any
  variables?: any
  extraOptions?: any[]
  fetchPolicy?: WatchQueryFetchPolicy
  context?: Context
  mapping: (data: any) => any
  filter?: (data: any) => any
}

interface FieldQueryProps {
  query: FieldQueryType
  onChange: (value?: any) => void
  As?: any
  id?: string
  label?: ReactNode
  input?: any
  touched?: boolean
  error?: string
  placeholder?: string
  isCheckbox?: boolean
  fieldError?: boolean
  labelText?: ReactNode
  scrollSelector?: string
}

let timeoutId: NodeJS.Timeout

function FieldQuery({
  As,
  id,
  label,
  input,
  touched,
  error,
  onChange,
  isCheckbox,
  fieldError,
  labelText,
  scrollSelector,
  ...rest
}: FieldQueryProps) {
  const { t } = useTranslation()

  const createOptions = useCallback(
    (data = {}, hasValue = false) => {
      const [allKey, selectedKey] = Object.keys(data)

      if (!data[allKey]) {
        return []
      }

      let edges = data[allKey].edges || []

      if (rest.query.filter) {
        edges = edges.filter(rest.query.filter)
      }

      const mapEdge = (edge: any) => ({
        ...rest.query.mapping(edge),
        node: edge.node ?? {},
      })

      if (hasValue && typeof data[selectedKey] !== 'undefined') {
        let selectedEdges = data[selectedKey].edges || []

        if (rest.query.filter) {
          selectedEdges = selectedEdges.filter(rest.query.filter)
        }

        return sortBy(
          uniqBy(
            [...selectedEdges.map(mapEdge), ...edges.map(mapEdge)],
            'value',
          ),
          'disabled',
        )
      }

      return edges.map(mapEdge)
    },
    [rest.query.filter, rest.query.mapping],
  )

  const [variables, setVariables] = useState(rest.query.variables)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    setVariables(rest.query.variables)
  }, [rest.query.variables])

  let selected: string[]
  if (As === FilterDropdown) {
    selected = isString(input.value) ? input.value.split(',') : input.value
  } else {
    selected = castArray(input.value)
  }

  const variablesWithSelected = useMemo(
    () => ({
      ...(variables || {}),
      selected: isArray(selected)
        ? selected
            .filter(id =>
              id.match(
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
              ),
            )
            .filter(id =>
              rest.query.extraOptions
                ? !some(rest.query.extraOptions, { value: id })
                : !!id,
            )
        : [],
    }),
    [variables, rest.query.extraOptions, selected],
  )

  const {
    data: newData,
    previousData,
    loading,
    fetchMore,
  } = useQuery(
    searchQuery.length && rest.query.searchQuery
      ? rest.query.searchQuery
      : rest.query.query,
    {
      fetchPolicy: rest.query.fetchPolicy || 'cache-and-network',
      variables: variablesWithSelected,
      context: rest.query.context,
      client: batchClient,
    },
  )

  const data = newData ?? previousData ?? {}

  const reachedBottom = () => {
    makeLoadMore(
      fetchMore,
      data,
      isPlainObject(data) ? Object.keys(data)[0] : '',
    )(variablesWithSelected)
  }

  const scrollContainerSelector = scrollSelector ?? `#${id} [role="listbox"]`

  const hasNextPage = data?.[Object.keys(data)[0]]?.pageInfo?.hasNextPage

  const allOptions = useMemo(
    () => [
      ...(isArray(rest.query.extraOptions) ? rest.query.extraOptions : []),
      ...createOptions(data, !!input.value.length),
    ],
    [rest.query.extraOptions, createOptions, data, input.value],
  )

  useEffect(() => {
    if (allOptions.length < 20) {
      reachedBottom()
    }
  }, [allOptions])

  return (
    <Form.Field>
      {label}
      <div>
        {
          <As
            onOpen={() => {
              setTimeout(() => {
                const scrollContainer = document.querySelector(
                  scrollSelector ?? `#${id} [role="listbox"]`,
                )

                if (
                  scrollContainer &&
                  hasNextPage &&
                  !loading &&
                  scrollContainer?.clientHeight >=
                    scrollContainer?.scrollHeight - 100
                ) {
                  reachedBottom()
                }
              }, 0)
            }}
            selectOnBlur={false}
            {...input}
            {...rest}
            placeholder={
              loading && !allOptions.length
                ? `${t('Loading')}...`
                : rest.placeholder ?? t('Select')
            }
            reachedBottom={reachedBottom}
            id={id}
            options={allOptions}
            searchQuery={searchQuery}
            onChange={(...args: any) => {
              const scrollContainer = document.querySelector(
                scrollContainerSelector,
              )
              if (
                scrollContainer &&
                hasNextPage &&
                !loading &&
                scrollContainer?.clientHeight >=
                  scrollContainer?.scrollHeight - 100
              ) {
                reachedBottom()
              }

              if (searchQuery?.length) {
                setSearchQuery('')
                setVariables({
                  ...variables,
                  search: '',
                })
              }

              onChange(...args)
            }}
            onSearchChange={(
              e: any,
              { searchQuery: searchQueryValue }: { searchQuery: string },
            ) => {
              // Search func doesn't not fire if the input is empty, so we have to refetch here
              if (searchQuery === searchQueryValue) {
                return null
              }

              setSearchQuery(searchQueryValue)

              // Defer refetch to prevent mass requests
              clearTimeout(timeoutId)
              timeoutId = setTimeout(() => {
                const searchFilter = buildSearch(
                  rest.query.searchFields,
                  [],
                  searchQueryValue,
                )
                setVariables({
                  ...variables,
                  search: searchQueryValue,
                  filter: {
                    ...variables?.filter,
                    ...searchFilter,
                  },
                })
              }, 300)
            }}
            search={(options: any) =>
              options.filter(({ text }: { text?: string }) =>
                text?.toLowerCase()?.includes(searchQuery?.toLowerCase()),
              )
            }
            onBlur={() => {
              if (searchQuery?.length) {
                setSearchQuery('')
                setVariables({
                  ...variables,
                  search: '',
                })
              }
            }}
            noResultsMessage={
              loading ? `${t('Loading')}...` : t('No results found')
            }
          />
        }
        {touched && error && error.length && <ErrorLabel error={error} />}
      </div>

      {id && (
        <ScrollDetector
          loading={loading}
          selector={scrollContainerSelector}
          onBottom={reachedBottom}
        />
      )}
    </Form.Field>
  )
}

export default FieldQuery
